import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../kodanza-logo.png"; // 🡆 file lives in /public
import { Menu, X } from "lucide-react"; // uses lucide icons (optional)

const ACCENT_TEXT   = "text-green-400";
const ACCENT_BORDER = "border-green-400";
const ACCENT_BG     = "bg-green-500";

export default function Navbar() {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) =>
    path === "/" ? loc.pathname === "/" : loc.pathname.startsWith(path);

  const linkClasses = (active) =>
    active
      ? `${ACCENT_TEXT} ${ACCENT_BORDER} border-b-2`
      : "text-[#8b949e] hover:text-[#c9d1d9]";

  const label = (() => {
    if (loading) return "Loading…";
    if (!user) return "Sign In";
    if (user.name) return user.name.split(" ")[0];
    if (user.email) return user.email.split("@")[0];
    return "Account";
  })();

  const navLinks = (
    <>
      <Link to="/" className={linkClasses(isActive("/"))}>Home</Link>
      <Link to="/pricing" className={linkClasses(isActive("/pricing"))}>Pricing</Link>
      <Link to="/lessons" className={linkClasses(isActive("/lessons"))}>Lessons</Link>
    </>
  );

  const authLink = loading ? (
    <span className="px-4 py-2 text-[#8b949e] animate-pulse">{label}</span>
  ) : user ? (
    <div className="relative group">
      <button className="px-4 py-2 border border-[#30363d] rounded-full text-[#c9d1d9] hover:border-green-400 transition">
        {label}
      </button>
      <div className="absolute right-0 mt-2 w-32 bg-[#161b22] border border-[#30363d] rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition">
        <button
          onClick={logout}
          className="block w-full text-left px-4 py-2 text-[#c9d1d9] hover:bg-[#21262d]"
        >
          Log out
        </button>
      </div>
    </div>
  ) : (
    <Link
      to="/signin"
      className={`px-4 py-2 rounded-full font-semibold transition border ${ACCENT_BORDER} ${ACCENT_TEXT} hover:${ACCENT_BG} hover:text-black`}
    >
      {label}
    </Link>
  );

  return (
    <nav className="sticky top-0 z-20 bg-[#0d1117]/80 backdrop-blur border-b border-[#30363d] font-mono">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src={Logo}
            alt="Kodanza"
            className="h-8 w-auto select-none"
            draggable="false"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium">
          {navLinks}
          {authLink}
        </div>

        {/* Mobile menu icon */}
        <button
          className="md:hidden text-[#c9d1d9]"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden px-6 pb-4 space-y-4 text-sm font-medium bg-[#0d1117] border-t border-[#30363d]">
          <div className="flex flex-col gap-3 pt-4">
            <Link to="/" onClick={() => setMobileOpen(false)} className={linkClasses(isActive("/"))}>Home</Link>
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className={linkClasses(isActive("/pricing"))}>Pricing</Link>
            <Link to="/lessons" onClick={() => setMobileOpen(false)} className={linkClasses(isActive("/lessons"))}>Lessons</Link>
            {!user ? (
              <Link
                to="/signin"
                onClick={() => setMobileOpen(false)}
                className={`mt-2 px-4 py-2 rounded-full text-center font-semibold border ${ACCENT_BORDER} ${ACCENT_TEXT} hover:${ACCENT_BG} hover:text-black`}
              >
                Sign In
              </Link>
            ) : (
              <button
                onClick={() => {
                  logout();
                  setMobileOpen(false);
                }}
                className="text-left px-4 py-2 rounded hover:bg-[#21262d] text-[#c9d1d9]"
              >
                Log Out
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
